import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
} from "@mui/material";
import { authHeader } from "../auth";

// Classement télémark (qualification, courses parallèles) enrichi des
// pénalités style/jump — voir telemarkResultsRouter.js.
// URL : /timingsoftware/resultats-telemark/:eventKey
//
// Colonnes Inter 1/2/3 réservées pour une capture future des temps
// intermédiaires — affichées vides ("—") pour l'instant, à dessein.

const POLL_MS = 3000;

function formatMs(ms) {
    if (ms == null) return "—";
    const totalCentis = Math.round(ms / 10);
    const minutes = Math.floor(totalCentis / 6000);
    const seconds = Math.floor((totalCentis % 6000) / 100);
    const centis = totalCentis % 100;
    const pad = (n) => String(n).padStart(2, "0");
    return minutes > 0 ? `${minutes}:${pad(seconds)}.${pad(centis)}` : `${seconds}.${pad(centis)}`;
}

function formatSeconds(s) {
    if (s == null) return "—";
    return `${Number(s).toFixed(2)}s`;
}

export default function TelemarkResults() {
    const { eventKey } = useParams();
    const [ranking, setRanking] = useState([]);
    const [error, setError] = useState("");

    const apiFetch = useCallback(
        (url, options = {}) =>
            fetch(url, {
                ...options,
                headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) },
            }),
        []
    );

    const load = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/timing/results-telemark/${encodeURIComponent(eventKey)}`);
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }
            setError("");
            const data = await res.json();
            setRanking(data.ranking || []);
        } catch (err) {
            setError(err.message);
        }
    }, [apiFetch, eventKey]);

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        return () => clearInterval(t);
    }, [load]);

    return (
        <Box p={3}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
                Résultats télémark — {eventKey}
            </Typography>

            {error && (
                <Typography color="error" mb={2}>
                    {error}
                </Typography>
            )}

            <Paper variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>Rang</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Dossard</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Nom</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Inter 1</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Inter 2</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Inter 3</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Style</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Jump</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Ski Time</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Total Time</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ranking.map((r, i) => (
                            <TableRow
                                key={r.bib ?? i}
                                sx={{ backgroundColor: i % 2 === 0 ? "#fafafa" : "transparent" }}
                            >
                                <TableCell sx={{ fontWeight: "bold" }}>{r.rank ?? "—"}</TableCell>
                                <TableCell>{r.bib ?? "—"}</TableCell>
                                <TableCell>{r.name || "—"}</TableCell>
                                <TableCell>{formatMs(r.inter1)}</TableCell>
                                <TableCell>{formatMs(r.inter2)}</TableCell>
                                <TableCell>{formatMs(r.inter3)}</TableCell>
                                <TableCell>{formatSeconds(r.stylePenalty)}</TableCell>
                                <TableCell>{formatSeconds(r.jumpPenalty)}</TableCell>
                                <TableCell>{formatMs(r.skiTimeMs)}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{formatMs(r.totalTimeMs)}</TableCell>
                            </TableRow>
                        ))}
                        {ranking.length === 0 && !error && (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    Aucun résultat pour l'instant.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
}